import { useParams } from "react-router-dom";
import { Spin } from "antd";
import { useGetAdvertisementById } from "services/advertisement";
import {
  AdvertisementLayoutType,
  AdvertisementLayoutPosition,
} from "types/advertisement";
import ImageSlider from "./ImageSlider";
import TextSlider from "./TextSlider";

const AdvertisementPage = () => {
  const { id } = useParams<{ id: string }>();
  const { advertisement, isLoading, error } = useGetAdvertisementById(id);

  if (isLoading) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-white">
        <Spin size="large" />
      </div>
    );
  }

  if (error || !advertisement) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-white">
        <span className="text-gray-500">Advertisement not found</span>
      </div>
    );
  }

  const { layoutType, layoutPosition, images, texts } = advertisement;

  const hasImages = images && images.length > 0;
  const hasTexts = texts && texts.length > 0;

  // Full screen image only
  if (hasImages && !hasTexts) {
    return (
      <div className="w-screen h-screen bg-white">
        <ImageSlider images={images} className="w-full h-full" />
      </div>
    );
  }

  // Full screen text only
  if (!hasImages && hasTexts) {
    return (
      <div className="w-screen h-screen bg-white">
        <TextSlider texts={texts} className="w-full h-full" />
      </div>
    );
  }

  // No content
  if (!hasImages && !hasTexts) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-white">
        <span className="text-gray-500">No content available</span>
      </div>
    );
  }

  const isHorizontal = layoutType === AdvertisementLayoutType.HORIZONTAL;
  const isImageFirst =
    layoutPosition === AdvertisementLayoutPosition.IMAGE_LEFT ||
    layoutPosition === AdvertisementLayoutPosition.IMAGE_TOP;

  const ImageComponent = (
    <ImageSlider images={images} className="w-full h-full" />
  );

  const TextComponent = (
    <TextSlider texts={texts} className="w-full h-full" />
  );

  if (isHorizontal) {
    return (
      <div className="w-screen h-screen flex bg-white">
        {isImageFirst ? (
          <>
            <div className="w-1/2 h-full">{ImageComponent}</div>
            <div className="w-1/2 h-full">{TextComponent}</div>
          </>
        ) : (
          <>
            <div className="w-1/2 h-full">{TextComponent}</div>
            <div className="w-1/2 h-full">{ImageComponent}</div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="w-screen h-screen flex flex-col bg-white">
      {isImageFirst ? (
        <>
          <div className="w-full h-1/2">{ImageComponent}</div>
          <div className="w-full h-1/2">{TextComponent}</div>
        </>
      ) : (
        <>
          <div className="w-full h-1/2">{TextComponent}</div>
          <div className="w-full h-1/2">{ImageComponent}</div>
        </>
      )}
    </div>
  );
};

export default AdvertisementPage;
